import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as ChargingStationIcon } from '../../../assets/icons/sidebar/sidebar-station.svg';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { AvailableStation } from '../../../interface/partner-interfaces/location-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { updateChargingPointFilters } from '../../../redux/slices/DeviceManagementSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { updateSingleStation } from '../../../redux/slices/Partners/ApprovedLocationManagementSlice';
import { updateDeviceFilters } from '../../../redux/slices/Partners/DeviceManagementSlice';
import { fetchPendingNotification } from '../../../redux/slices/Partners/PendingLocationManagementSlice';
import { updateChargingPointFilters as updatePersonalChargingPointFilters } from '../../../redux/slices/PersonalDeviceSlice';
import DisableSwitchWarning from '../../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../shared-components/switch-button-field/SwitchButton';
import TabMenus from '../../../shared-components/tab-menus/TabMenus';
import { getCookie } from '../../../shared-functions/Cookies';
import { getMyAccountCustomId, permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import { getLocalStorageItem } from '../../../shared-functions/LocalStorage';
import deviceApi from '../../../utils/api/partner/device-partner';
import stationApi from '../../../utils/api/partner/station';

import ApprovedList from './components/ApprovedList';
import PendingList from './components/PendingList';
import RejectedList from './components/RejectedList';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
  closeModal: () => void;
}

interface StatusCountsInterface {
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
  totalStationsCount: number;
  totalDeviceCount: number;
  totalOnlineDeviceCount: number;
  totalOfflineDeviceCount: number;
}

interface StationManagementProperties {
  preventNavigation?: boolean;
  fetchByPartner?: string;
  onSuccess?: () => void;
}

/**
 * Station management page
 * @param {StationManagementProperties} properties - Component properties
 * @param {boolean} [properties.preventNavigation] - The preventNavigation prop
 * @param {string} [properties.fetchByPartner] - The fetchByPartner prop
 * @param {Function} [properties.onSuccess] - The onSuccess callback prop
 * @returns {any} The rendered station management page
 */
function StationManagement({
  preventNavigation = false,
  fetchByPartner,
  onSuccess,
}: StationManagementProperties) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentPage } = useParams<{ currentPage: string }>();
  const { t, i18n } = useTranslation('location');
  const [openTab, setOpenTab] = useState<number>(0);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [statusCounts, setStatusCounts] = useState<StatusCountsInterface>();

  const { portalType, activePortal } = usePortal();
  const isPartnerPortal =
    portalType === PortalType.PARTNER ||
    activePortal === PortalType.PARTNER ||
    permissionCheck('partnerChargingStationView');

  const queryAccountCustomId = searchParameters.get('accountCustomId');
  const storedPartnerId =
    getMyAccountCustomId() ||
    getLocalStorageItem('accountCustomId') ||
    getCookie('accountCustomId') ||
    '';

  const effectivePartnerId =
    fetchByPartner ||
    (isPartnerPortal ? storedPartnerId || queryAccountCustomId || '' : '') ||
    (storedPartnerId && !permissionCheck('cpoChargingStationView') ? storedPartnerId : '') ||
    (queryAccountCustomId && !permissionCheck('cpoChargingStationView') ? queryAccountCustomId : '');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
    /**
     * Close modal
     */
    closeModal: () => setModalBoxStatus('close'),
  });

  /**
   * Fetch approved, pending, rejected stations and device counts
   * @returns {Promise<void>}
   */
  const fetchStatusCounts = async () => {
    try {
      const accountFilter = effectivePartnerId ? { accountCustomIds: [effectivePartnerId] } : {};

      const [approvedResponse, pendingResponse, rejectedResponse] = await Promise.all([
        stationApi.fetchStations({ limit: 1, submissionStatus: 'approved', ...accountFilter }),
        stationApi.fetchStations({ limit: 1, submissionStatus: 'pending', ...accountFilter }),
        stationApi.fetchStations({ limit: 1, submissionStatus: 'rejected', ...accountFilter }),
      ]);

      const approved = approvedResponse?.data?.totalResults ?? 0;
      const pending = pendingResponse?.data?.totalResults ?? 0;
      const rejected = rejectedResponse?.data?.totalResults ?? 0;

      let devices = 0;
      let online = 0;
      let offline = 0;

      if (effectivePartnerId?.length) {
        // `chargerd-status-count` doesn't honor account scoping on the backend,
        // so derive the totals from the account-filterable device list endpoint instead.
        const [totalDeviceResponse, onlineDeviceResponse, offlineDeviceResponse] =
          await Promise.all([
            deviceApi.fetchDevices({ ...accountFilter }),
            deviceApi.fetchDevices({ ...accountFilter, deviceStatus: 'online' }),
            deviceApi.fetchDevices({ ...accountFilter, deviceStatus: 'offline' }),
          ]);
        devices = totalDeviceResponse?.data?.totalResults ?? 0;
        online = onlineDeviceResponse?.data?.totalResults ?? 0;
        offline = offlineDeviceResponse?.data?.totalResults ?? 0;
      } else {
        const deviceResponse = await deviceApi.fetchDeviceStatusCount();
        devices = deviceResponse?.data?.totalDeviceCount ?? 0;
        online = deviceResponse?.data?.totalOnlineDeviceCount ?? 0;
        offline = deviceResponse?.data?.totalOfflineDeviceCount ?? 0;
      }

      setStatusCounts({
        approvedCount: approved,
        pendingCount: pending,
        rejectedCount: rejected,
        totalStationsCount: approved + pending + rejected,
        totalDeviceCount: devices,
        totalOnlineDeviceCount: online,
        totalOfflineDeviceCount: offline,
      });
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message:
            error?.response?.data?.message ||
            error?.message ||
            t('somethingWentWrong'),
          code: error?.response?.data?.code || error?.response?.status || 0,
        }),
      );
    }
  };

  const { totalResults } = useAppSelector((state) => state.partnersApprovedLocation);
  const { totalResults: unapprovedTotalResults } = useAppSelector(
    (state) => state.partnersPendingLocation,
  );
  const { totalResults: rejectedTotalResults } = useAppSelector(
    (state) => state.partnersRejectLocation,
  );
  /**
   * Confirm the status change function
   * @param {string} id - The id of the station
   * @param {boolean} checked - The checked availability status
   * @returns {void}
   */
  const confirmStatusChange = (id: string, checked: boolean) => {
    stationApi
      .editAvailableStatusStation(id, { availabilityStatus: checked })
      .then((response) => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          content: (
            <SuccessNotification
              title={t('statusChangedTitle')}
              description={t('statusChangedDescription')}
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                dispatch(updateSingleStation(response.data));
                fetchStatusCounts();
                onSuccess?.();
              }}
            />
          ),
          notificationModal: true,
          header: false,
          /**
           * Close modal with function to dispatch station details
           */
          closeModal: () => {
            setModalBoxStatus('close');
            dispatch(updateSingleStation(response.data));
            fetchStatusCounts();
            onSuccess?.();
          },
        });
      })
      .catch((error) => {
        setModalBoxStatus('close');
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            message:
              error?.response?.data?.message ||
              error?.message ||
              t('somethingWentWrong', 'Something went wrong!'),
            code: error?.response?.data?.code || error?.response?.status || 0,
          }),
        );
      });
  };

  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when enabling or disabling a station
   * @param {AvailableStation} device - The device to update
   * @param {boolean} checked - The new availability status
   * @returns {void}
   */
  const handleAvailabilityChange = (device: AvailableStation, checked: boolean) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: checked
        ? t('enableWarningTitle')
        : t('disableWarningTitle'),
      content: (
        <DisableSwitchWarning
          selectedId={device._id}
          onCancel={() => setModalBoxStatus('close')}
          message={
            checked
              ? t('enableWarningMessage')
              : t('disableWarningMessage')
          }
          onConfirm={() => confirmStatusChange(device._id, checked)}
        />
      ),
      notificationModal: true,
      header: false,
      /**
       * Close modal
       */
      closeModal: () => setModalBoxStatus('close'),
    });
  };

  /**
   * Renders the custom data for the availability column
   * @param {AvailableStation} device - The device data
   * @returns {any} The rendered availability toggle cell
   */
  const renderAvailability = (device: AvailableStation) => {
    return (
      <div
        role="presentation"
        className="flex justify-center"
        onClick={(event) => event.stopPropagation()} // 🔥 prevents row click
        onMouseDown={(event) => event.stopPropagation()} // extra safety
      >
        <div role="presentation" key={device._id} onClick={(event) => event.stopPropagation()}>
          <SwitchButton
            checked={device.isAvailable}
            disabled={!permissionCheck('cpoChargingStationUpdate')}
            change={(checked: boolean) => {
              if (!permissionCheck('cpoChargingStationUpdate')) {
                return;
              }
              handleAvailabilityChange(device, checked);
            }}
          />
        </div>
      </div>
    );
  };

  /**
   * Handle the tab change function
   * @param {any} number - The number
   */
  const changeTabPages = (number: number) => {
    setOpenTab(number);
    if (preventNavigation) {
      return;
    }
    let pageName = '';
    switch (number) {
      case 0:
        pageName = 'approved';
        break;
      case 1:
        pageName = 'unapproved';
        break;
      case 2:
        pageName = 'rejected';
        break;

      default:
        break;
    }
    // navigate(`/ev-admin/charging-location/${pageName}`);
    // window.history.pushState({}, '', `${pageName}`);
    navigate(`../${pageName}`); // ✅ React Router method
  };

  /**
   * Handles navigation to the chargers page with the selected device status filter (online / offline).
   * @param {'online' | 'offline'} status - The charger status to filter by.
   * @returns {void}
   */
  const handleNavigateToChargers = (status: 'online' | 'offline'): void => {
    const filterValue = [status];

    dispatch(
      updateDeviceFilters({
        key: 'deviceStatus',
        value: filterValue,
        type: 'restAndAdd',
      }),
    );
    if (effectivePartnerId) {
      dispatch(
        updateDeviceFilters({
          key: 'accountCustomIds',
          value: [effectivePartnerId],
          type: 'replace',
        }),
      );
    }
    dispatch(
      updateChargingPointFilters({
        key: 'deviceStatus',
        value: filterValue,
        type: 'restAndAdd',
      }),
    );
    dispatch(
      updatePersonalChargingPointFilters({
        key: 'deviceStatus',
        value: filterValue,
        type: 'restAndAdd',
      }),
    );

    navigate({
      pathname: '/ev-admin/operations/chargers',
      search: `?${new URLSearchParams({
        deviceStatus: status,
        ...(effectivePartnerId ? { accountCustomId: effectivePartnerId } : {}),
      })}`,
    });
  };
  useEffect(() => {
    if (preventNavigation) {
      return;
    }
    if (currentPage) {
      let title = '';
      let count = 0;
      switch (currentPage) {
        case 'approved':
          title = t('chargingStation');
          count = totalResults;
          break;
        case 'unapproved':
          title = t('chargingStation');
          count = unapprovedTotalResults;
          break;
        case 'rejected':
          title = t('chargingStation');
          count = rejectedTotalResults;
          break;
        default:
          break;
      }
      dispatch(setPageTitle({ title, icon: <ChargingStationIcon />, count }));
    }
  }, [totalResults, unapprovedTotalResults, rejectedTotalResults, preventNavigation]);
  useEffect(() => {
    if (preventNavigation) {
      return;
    }
    if (currentPage) {
      let number = 0;
      switch (currentPage) {
        case 'approved':
          number = 0;
          break;
        case 'unapproved':
          number = 1;
          break;
        case 'rejected':
          number = 2;
          break;

        default:
          break;
      }
      setOpenTab(number);
    }
  }, [preventNavigation]);
  useEffect(() => {
    dispatch(fetchPendingNotification());
  }, []);

  useEffect(() => {
    if (preventNavigation) {
      return;
    }
    dispatch(
      setPageTitle({
        title: t('chargingStation'),
        icon: <ChargingStationIcon />,
        count: totalResults,
      }),
    );
  }, [t, preventNavigation, totalResults]);

  useEffect(() => {
    if (
      searchParameters.get('stationId') ||
      searchParameters.get('deviceId') ||
      (!window.location.pathname.includes('payout-management') && !isPartnerPortal &&
        searchParameters.get('accountCustomId'))
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('deviceId');
      if (!window.location.pathname.includes('payout-management') && !isPartnerPortal) {
        parameters.delete('accountCustomId');
      }
      setSearchParameters(parameters, { replace: true });
    }
  }, [isPartnerPortal]);

  useEffect(() => {
    fetchStatusCounts();
  }, [currentPage, effectivePartnerId]);

  return (
    <div className="location-management-wrap" dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={modalBoxDetail.closeModal}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />

      {/* Stats Summary Bar */}
      <div className="stats-bar">
        <div className="stat-item" key="total-stations">
          <span className="stat-label">{t('totalStations')}:</span>
          <span className="stat-value">{statusCounts?.totalStationsCount ?? 0}</span>
        </div>

        <div className="stat-divider" />

        <div className="table-tab">
          <TabMenus
            menus={[
              {
                name: t('approved'),
                count: statusCounts?.approvedCount || undefined,
              },
              {
                name: t('pending'),
                count: statusCounts?.pendingCount ?? 0,
              },
              {
                name: t('rejected'),
                count: statusCounts?.rejectedCount || undefined,
              },
            ]}
            openTab={openTab}
            setOpenTab={changeTabPages}
          />
        </div>

        <div className="stat-divider" />

        <div className="stat-item" key="total-devices">
          <span className="stat-label">{t('totalDevices')}:</span>
          <span className="stat-value">{statusCounts?.totalDeviceCount ?? 0}</span>
        </div>

        <div className="stat-divider" />

        <div
          role="button"
          tabIndex={0}
          className="stat-badge online-badge"
          key="online"
          onClick={() => handleNavigateToChargers('online')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleNavigateToChargers('online');
            }
          }}>
          <span className="badge-dot" />
          {t('onlineChargers')}: {statusCounts?.totalOnlineDeviceCount ?? 0}
        </div>

        <div
          role="button"
          tabIndex={0}
          className="stat-badge offline-badge"
          key="offline"
          onClick={() => handleNavigateToChargers('offline')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleNavigateToChargers('offline');
            }
          }}>
          <span className="badge-dot" />
          {t('offlineChargers')}: {statusCounts?.totalOfflineDeviceCount ?? 0}
        </div>
      </div>

      <div className="table-container">
        {openTab === 0 && (
          <ApprovedList
            renderAvailability={renderAvailability}
            preventNavigation={preventNavigation}
            fetchByPartner={effectivePartnerId}
            updateCounts={fetchStatusCounts}
            onSuccess={() => {
              fetchStatusCounts();
              onSuccess?.();
            }}
          />
        )}
        {openTab === 1 && (
          <PendingList
            preventNavigation={preventNavigation}
            fetchByPartner={effectivePartnerId}
            updateCounts={fetchStatusCounts}
            onSuccess={() => {
              fetchStatusCounts();
              onSuccess?.();
            }}
          />
        )}
        {openTab === 2 && (
          <RejectedList
            preventNavigation={preventNavigation}
            fetchByPartner={effectivePartnerId}
            updateCounts={fetchStatusCounts}
            onSuccess={() => {
              fetchStatusCounts();
              onSuccess?.();
            }}
          />
        )}
      </div>
    </div>
  );
}

StationManagement.defaultProps = {
  preventNavigation: false,
  fetchByPartner: '',
  onSuccess: undefined,
};

export default StationManagement;
